/**
 * Created by macuser on 4/26/16.
 */

/**
 * A static utility class which generates error handler functions for the various types of errors which may occur in the application.
 * @module app/utils/ErrorUtils
 */

import _ from "underscore";
import s from "underscore.string";
import logger from "app/Logger";
import messenger from "app/Messenger";
function isPlainObject(obj) {
  return (
    obj &&
    !_.isFunction(obj) &&
    !_.isArray(obj) &&
    !_.isString(obj) &&
    _.isObject(obj)
  );
}

function parseErrorType(err, options) {
  var errorType = "An error";

  if (err instanceof Error) {
    errorType = "An unexpected error";
  } else if (options.ajax || (err && err.abort)) {
    if (err.status === 0) {
      errorType = "A timeout";
    } else if (err.responseType == "json") {
      errorType = parseErrorType(JSON.parse(err.response), options);
    } else if (err.responseJSON) {
      errorType = parseErrorType(err.responseJSON, options);
    } else if (_.isArray(err.errors)) {
      if (err.errors.length == 1) {
        errorType = "A network error";
      } else {
        errorType = "Multiple network errors";
      }
    } else {
      errorType = "A network error";
    }
  } else if (options.json || isPlainObject(err)) {
    errorType = "An API error";

    if (_.isArray(err.errors) && err.errors.length != 1) {
      errorType = "API errors";
    }
  } else if (_.isString(err) || _.isArray(err)) {
    errorType = "An error";
  } else if (err !== false) {
    //in some places, we use false to indicate an action was cancelled
    errorType = "An unknown error";
  }

  return errorType;
}

function parseCorrelationID(err) {
  var correlationId = "";

  if (err && _.isFunction(err.getResponseHeader)) {
    correlationId = err.getResponseHeader("x-correlation-id");
  } else if (isPlainObject(err)) {
    correlationId = err.id || "";
  }

  return correlationId;
}

function parseErrorMessage(err, options) {
  var message = null;
  var correlationId = "";

  if (err instanceof Error) {
    message = err.message;
  } else if (options.ajax || (err && err.abort)) {
    if (err.responseJSON) {
      message = parseErrorMessage(err.responseJSON, options);
    } else if (err.responseType == "json" && err.response) {
      message = parseErrorMessage(JSON.parse(err.response), options);
    } else if (_.isArray(err.errors)) {
      message = _.map(err.errors, function (err) {
        return parseErrorMessage(err, options);
      });

      if (message.length == 1) {
        message = message[0];
      }
    } else if (isPlainObject(err)) {
      if (_.isFunction(err.error)) {
        message = err.reason || err.message || err.title;
      } else {
        message = err.error || err.reason || err.message || err.title;
      }
    } else {
      message = err.responseText;
    }
  } else if (options.json || isPlainObject(err)) {
    if (_.isArray(err.errors)) {
      message = _.map(err.errors, function (err) {
        return parseErrorMessage(err, options);
      });

      if (message.length == 1) {
        message = message[0];
      }
    } else {
      if (_.isFunction(err.error)) {
        message = err.reason || err.message || err.title;
      } else {
        message = err.error || err.reason || err.message || err.title || err;
      }

      if (!_.isString(message)) {
        message = "Message could not be found.";
      }
    }
  } else if (_.isString(err)) {
    message = err;
  } else if (_.isArray(err)) {
    message = _.map(err, function (_err) {
      return parseErrorMessage(_err, options);
    });

    if (message.length == 1) {
      message = message[0];
    }
  }

  if (_.isString(message)) {
    message = s.humanize(message);
  }

  return message;
}

var ErrorUtils = {
  /**
   * Gets the current stack trace.
   * @returns {Array} The stack trace - each array element is a single stack frame.
   */
  stackTrace: function () {
    var e = new Error("dummy");

    /* istanbul ignore else */
    if (e.stack) {
      return e.stack
        .replace(/^[^\(]+?[\n$]/gm, "")
        .replace(/^\s+at\s+/gm, "")
        .replace(/^Object.<anonymous>\s*\(/gm, "{anonymous}()@")
        .split("\n");
    } else {
      return ["Stack trace not available in this browser."];
    }
  },

  /**
   * Normalizes an ambiguous error object. Attempts to produce an appropriate error object based on the given argument.
   * @param {Object} options A hash of options. May be null.
   * @param {String} [options.activity] A string detailing what the user was doing, e.g. "saving the object".
   * @param {Error|Object|String|jqXHR} err The error object.
   * @returns {Object} A normalized object representing the error.
   * @todo What is this object supposed to look like?
   */
  normalizeError: function (options, err) {
    var normalizedError = {
      stack: ErrorUtils.stackTrace(),
      originalError: err,
      _alreadyNormalized: true,
    };
    var activity = ".";
    options = options || {};

    if (options.activity) {
      activity = " " + options.activity;

      if (!s(activity).endsWith(".")) {
        activity = activity + ".";
      }
    }

    //need a message, a userMessage, an optional stack trace, anything else?
    if (!(err && err._alreadyNormalized)) {
      if (err === false) {
        //in some places, we use false to indicate an action was cancelled
        normalizedError = null;
      } else {
        var errorType = parseErrorType(err, options);
        var message = parseErrorMessage(err, options);
        var correlationId = parseCorrelationID(err);
        var userMessage = errorType + " occurred" + activity;
        var correlationMessage = correlationId
          ? " (include the following id when reporting the error: " +
            correlationId +
            ")"
          : "";

        if (message) {
          if (_.isArray(message)) {
            message = _.map(message, function (msg) {
              return '"' + msg + '"';
            });
            message = message.join(", ");
            userMessage =
              userMessage +
              " The messages were " +
              message +
              correlationMessage +
              ".";
          } else {
            userMessage =
              userMessage +
              ' The message was "' +
              message +
              correlationMessage +
              '".';
          }
        } else {
          message = userMessage;
        }

        if (err instanceof Error && err.stack) {
          normalizedError.stack = err.stack.split("\n");
        }

        normalizedError.message = message;
        normalizedError.userMessage = userMessage;
        normalizedError.correlationId = correlationId;
      }
    } else {
      normalizedError = err;
    }

    return normalizedError;
  },

  /**
   * Handles an ambiguous error object. Attempts to produce an appropriate error message based on the given argument.
   * @param {Object} options A hash of options. May be null.
   * @param {String} [options.activity] A string detailing what the user was doing, e.g. "saving the object".
   * @param {Boolean} [options.skipUser] True to skip sending notification of the error to the user, otherwise false.
   * @param {Function} [options.cleanUp] A function to execute after the error has been handled.
   * @param {Error|Object|String|jqXHR} err The error object.
   * @returns {Object} A normalized object representing the error.
   */
  handleError: function (options, err) {
    var normalizedError = ErrorUtils.normalizeError(options, err);

    options = _.defaults({}, options, {
      skipUser: false,
      cleanUp: function () {},
    });

    if (normalizedError && !normalizedError._handled) {
      logger.error(normalizedError);

      if (!options.skipUser) {
        messenger.error(normalizedError.userMessage);
      }

      normalizedError._handled = true;
    }

    options.cleanUp();

    return normalizedError;
  },

  /**
   * Handles a jqXHR error.
   * @param {Object} options A hash of options.
   * @param jqXHR Corresponds to the `jqXHR` argument in the jQuery documentation for ajax.
   * @param textStatus Corresponds to the `textStatus` argument in the jQuery documentation for ajax.
   * @param errorThrown Corresponds to the `errorThrown` argument in the jQuery documentation for ajax.
   * @returns {Object} A normalized object representing the error.
   */
  handleAjaxError: function (options, jqXHR, textStatus, errorThrown) {
    return ErrorUtils.handleError(
      _.extend({}, options, {
        ajax: true,
        textStatus: textStatus,
        errorThrown: errorThrown,
      }),
      jqXHR
    );
  },

  /**
   * Handles an API/Backbone error.
   * @param {Object} options A hash of options.
   * @param model Corresponds to the `model` or `collection` argument in the Backbone documentation for fetch.
   * @param responseBody Corresponds to the `response` argument in the Backbone documentation for fetch.
   * @param requestOptions Corresponds to the `options` argument in the Backbone documentation for fetch.
   * @returns {Object} A normalized object representing the error.
   */
  handleApiError: function (options, model, responseBody, requestOptions) {
    return ErrorUtils.handleError(
      _.extend(
        {},
        options,
        {
          json: true,
        },
        requestOptions
      ),
      responseBody
    );
  },

  /**
   * Creates a handler function which can handle a generic error argument.
   * @param {Object} [options] A hash of options to pass to the handler function.
   * @returns {Function} A function which accepts a single error argument. This function *will* call `context.app.logger.error` and may call `context.messenger.error`.
   */
  errorHandler: function (options) {
    return function (err) {
      return ErrorUtils.handleError(options, err);
    };
  },

  /**
   * Creates a handler function which can handle the arguments passed to the error function of a jqXHR object.
   * @param {Object} [options] A hash of options to pass to the handler function.
   * @returns {Function} A function which accepts `jqXHR`, `textStatus`, and `errorThrown` arguments, as specified in http://api.jquery.com/jQuery.ajax/.
   */
  ajaxHandler: function (options) {
    return function (jqXHR, textStatus, errorThrown) {
      return ErrorUtils.handleAjaxError(
        options,
        jqXHR,
        textStatus,
        errorThrown
      );
    };
  },

  /**
   * Creates a handler function which can handle the arguments passed to the error function of a Backbone.Model.fetch or Backbone.Collection.fetch options argument.
   * @param {Object} [options] A hash of options to pass to the handler function.
   * @returns {Function} A function which accepts arguments corresponding to those mentioned here http://backbonejs.org/#Model-fetch.
   */
  apiHandler: function (options) {
    return function (model, responseBody, requestOptions) {
      return ErrorUtils.handleApiError(
        options,
        model,
        responseBody,
        requestOptions
      );
    };
  },
};

export default ErrorUtils;



//////////////////
// WEBPACK FOOTER
// ./public/js/app/utils/ErrorUtils.js
// module id = 12
// module chunks = 1